<script lang="ts" setup>
import { cva } from 'class-variance-authority'
import { camelCase } from 'lodash'
import useTithelySSO from '@/composables/sso'
import type { TithelyNavSettingsItem } from '@/types/nav/tithelyNav'
import { SpBadge } from '@tithely/sproutkit-vue'

type Props = TithelyNavSettingsItem & {
    mobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    mobile: false
})

const { redirect } = useTithelySSO()

const emit = defineEmits<{
    action: [string]
}>()

const mainClasses = cva(`flex`, {
    variants: {
        mobile: {
            true: `flex-col !px-8 !py-3`,
            false: `items-center gap-2 !p-2 hover:bg-primary/10`
        }
    }
})

const labelClasses = cva(`!my-0 font-sans text-base/normal font-medium tracking-[-2%]`, {
    variants: {
        mobile: {
            false: `text-primary`
        }
    }
})

const descriptionClasses = cva(`!my-0 font-sans tracking-[-2%]`, {
    variants: {
        mobile: {
            true: `text-xs/normal`,
            false: `text-sm/normal`
        }
    }
})

const action = () => {
    switch (true) {
        case !!props.sso:
            redirect(props.sso)
            break
        case !!props.action:
            emit(`action`, props.action)
            break
        case !!props.route:
            window.open(props.route, `_self`)
    }
}
</script>

<template>
    <a
        :href="props.route || `#`"
        :class="mainClasses({ mobile })"
        @click.prevent="action">
        <div
            v-if="!mobile"
            class="flex w-12 shrink-0 grow-0 items-center justify-center">
            <Component
                :is="props.icon"
                weight="duotone"
                class="size-6 text-primary" />
        </div>

        <div class="flex flex-col items-start justify-center">
            <h4 :class="labelClasses({ mobile })">
                {{ $t(`nav.labels.${camelCase(props.label)}`) }}

                <SpBadge
                    v-if="props.badge"
                    size="sm"
                    intent="info">
                    {{ $t(`nav.badges.${props.badge}`) }}
                </SpBadge>
            </h4>

            <p :class="descriptionClasses({ mobile })">
                {{ $t(`nav.descriptions.${camelCase(props.label)}`) }}
                <span
                    v-if="props.tag"
                    class="text-blue-600 underline hover:text-blue-800">
                    {{ $t(`nav.tags.${props.tag}`) }}
                </span>
            </p>
        </div>
    </a>
</template>
